import {
  AdminAppAction,
  PageActionType,
  UpdatePageErrorAction,
  UpdatePageRequestAction,
  UpdatePageSuccessAction,
  popAdminPanel,
  updatePageError,
  updatePageSuccess,
} from '../../actions';
import { Epic, ofType } from 'redux-observable';
import { LIST_PAGES_QUERY, ListPagesData } from '../../queries';
import { Observable, of } from 'rxjs';
import {
  UPDATE_PAGE_MUTATION,
  UpdatePageInput,
  UpdatePagePayload,
  UpdatePageVariables,
} from '../../mutations';
import { getImage, getPage, getPageTagsArray } from '../../selectors';
import { mergeAll, switchMap } from 'rxjs/operators';
import { AdminAppState } from '../../state';
import { EpicDependencies } from '../epic-dependencies';
import { MutationUpdaterFn } from 'apollo-client';
import { getMessenger } from '../../components/admin/messenger';
import { identity } from 'lodash';

const stateToVariables = (
  state: AdminAppState,
  pageKey: string,
): UpdatePageVariables => {
  const {
    categoryIds,
    content,
    contentType,
    description,
    discourageIndexing,
    path,
    pinterestDescription,
    posterImages,
    tagIds,
    title,
    id,
    publishAt,
  } = getPage(pageKey)(state);

  const images = posterImages
    .map(({ imageKey, type }) => {
      const image = getImage(imageKey)(state);
      if (image.id) {
        return { imageId: image.id, type };
      } else {
        return null;
      }
    })
    .filter(identity) as UpdatePageInput['images'];

  return {
    id,
    input: {
      categoryIds,
      content: content as any,
      contentType: contentType || undefined,
      description,
      discourageIndexing,
      images,
      path: path ? path.trim() : undefined,
      pinterestDescription,
      publishAt: publishAt,
      tagIds,
      tags: getPageTagsArray(pageKey)(state),
      title,
    },
  };
};

const updatePageList = (
  variables: UpdatePageVariables,
): MutationUpdaterFn<UpdatePagePayload> => (cache, { data }) => {
  let query: any;

  try {
    query = cache.readQuery<ListPagesData>({
      query: LIST_PAGES_QUERY,
    });
  } catch (e) {
    return;
  }

  if (data && query) {
    const existingEdges =
      query.pages && query.pages.edges ? query.pages.edges : [];
    const edges = [...existingEdges];

    for (const edge of edges) {
      if (edge.node.id === variables.id) {
        edge.node = {
          ...edge.node,
          ...variables.input,
        };
      }
    }

    cache.writeQuery<ListPagesData>({
      query: LIST_PAGES_QUERY,
      data: {
        ...query,
        pages: { ...query.pages, edges },
      },
    });
  }
};

const updateCache = (
  variables: UpdatePageVariables,
): MutationUpdaterFn<UpdatePagePayload> => (cache, result) => {
  updatePageList(variables)(cache, result);
};

export const updatePageEpic = (action$, state, { client }) =>
  action$.pipe(
    ofType<AdminAppAction, UpdatePageRequestAction>(
      PageActionType.UpdatePageRequest,
    ),
    switchMap(async (action: UpdatePageRequestAction) => {
      const variables = stateToVariables(state.value, action.pageKey);
      try {
        const { data, errors } = await client.mutate({
          errorPolicy: 'all',
          mutation: UPDATE_PAGE_MUTATION,
          update: updateCache(variables),
          variables,
        });

        if (errors) {
          alert(
            'Error saving page. You may not be authorized, or the URL may already be taken. Try using a different URL path.',
          );
          return of(updatePageError(action.pageKey, errors));
        } else if (data) {
          getMessenger().send('save_page');
          return of(
            updatePageSuccess(action.pageKey, { redirect: action.redirect }),
            popAdminPanel(),
          );
        }
      } catch (e) {
        alert('Failed to save the page. Please try again...');
        return of(updatePageError(action.pageKey, []));
      }
    }),
    mergeAll(),
  );
